import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
    import supabase from '@/supabase/supabase';

    const AuthContext = createContext({});

    export const AuthProvider = ({ children }) => {
      const [user, setUser] = useState(null);
      const [profile, setProfile] = useState(null);
      const [loading, setLoading] = useState(true);
      
      // Track the current user ID to prevent unnecessary reloads when switching tabs
      const currentUserRef = useRef(null);

      const fetchProfile = async (currentUser, retries = 3) => {
        if (!currentUser?.id) return null;
        for (let i = 0; i < retries; i++) {
          try {
            const { data, error } = await supabase
              .from('profiles')
              .select('id, email, full_name, role, account_status, created_at, email_verified, welcome_sent')
              .eq('id', currentUser.id)
              .single();

            if (error) {
              if (error.code === 'PGRST116') {
                await new Promise(resolve => setTimeout(resolve, 800));
                continue;
              }
              console.warn('Profile fetch error:', error.message);
              break;
            }
            return data;
          } catch (err) {
            console.error('Profile fetch exception:', err);
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        
        try {
          const role = currentUser.user_metadata?.role || 'driver';
          const { data: newProfile, error: insertError } = await supabase.from('profiles').insert({
            id: currentUser.id,
            email: currentUser.email,
            full_name: currentUser.user_metadata?.full_name || 'User',
            role: role,
            account_status: 'active',
            email_verified: true
          }).select().single();

          if (!insertError && newProfile) {
            if (role === 'driver') {
              await supabase.from('driver_profiles').insert({
                user_id: currentUser.id,
                district: currentUser.user_metadata?.district || ''
              });
            } else if (role === 'business') {
              await supabase.from('business_profiles').insert({
                user_id: currentUser.id,
                business_name: currentUser.user_metadata?.business_name || ''
              });
            }
            return newProfile;
          }
        } catch (healError) {
          console.error('Auto-heal failed:', healError);
        }
        return null;
      };

      useEffect(() => {
        let mounted = true;

        const initialize = async () => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!mounted) return;
            if (session?.user) {
              currentUserRef.current = session.user.id;
              setUser(session.user);
              const profileData = await fetchProfile(session.user);
              if (mounted) setProfile(profileData);
            }
          } catch (err) {
            console.error('Auth initialization error:', err);
          } finally {
            if (mounted) setLoading(false);
          }
        };

        initialize();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (_event, session) => {
            if (!mounted) return;
            const currentUser = session?.user ?? null;

            // BUG FIX: Supabase fires 'SIGNED_IN' or 'TOKEN_REFRESHED' when returning to the tab.
            // If the user hasn't changed, we silently update the user token but DO NOT set loading to true.
            // This prevents the UI from unmounting and wiping unsaved form data.
            if (currentUser && currentUser.id === currentUserRef.current) {
              setUser(currentUser);
              return;
            }

            // A genuine auth change (Login, Logout, or Switching accounts)
            currentUserRef.current = currentUser?.id || null;
            setUser(currentUser);

            if (currentUser) {
              setLoading(true);
              const profileData = await fetchProfile(currentUser);
              if (mounted) {
                setProfile(profileData);
                setLoading(false);
              }
            } else {
              if (mounted) {
                setProfile(null);
                setLoading(false);
              }
            }
          }
        );

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
      }, []);

      const signUp = async (email, password, metadata) => {
        return supabase.auth.signUp({
          email,
          password,
          options: {
            data: metadata,
            emailRedirectTo: `${window.location.origin}/login`
          },
        });
      };

      const signIn = async (email, password) => {
        return supabase.auth.signInWithPassword({ email, password });
      };

      const signOut = async () => {
        setProfile(null);
        setUser(null);
        currentUserRef.current = null;
        return supabase.auth.signOut();
      };

      const resetPassword = async (email) => {
        return supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
      };

      return (
        <AuthContext.Provider value={{ user, profile, loading, signIn, signUp, signOut, resetPassword }}>
          {children}
        </AuthContext.Provider>
      );
    };

    export const useAuth = () => useContext(AuthContext);